import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { Jumbotron } from './migration';
import { Icon as Iconify } from '@iconify/react';
import { ThemeContext } from '../../App';
import { motion } from 'framer-motion';

const TechSkills = () => {
  const theme = useContext(ThemeContext).theme;
  const getIsMobile = () => (typeof window !== 'undefined' ? window.innerWidth < 992 : false);
  const MOBILE_VISIBLE_COUNT = 6;
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [showAll, setShowAll] = useState(() => !getIsMobile());

  const icons = useMemo(() => {
    return [
      { name: 'AWS', icon: `skill-icons:aws-${theme}` },
      { name: 'Azure', icon: `skill-icons:azure-${theme}` },
      { name: 'PowerShell', icon: `skill-icons:powershell-${theme}` },
      { name: 'Linux', icon: `skill-icons:linux-${theme}` },
      { name: 'Bash', icon: `skill-icons:bash-${theme}` },
      { name: 'React.js', icon: `skill-icons:react-${theme}` },
      { name: 'JavaScript', icon: `skill-icons:javascript` },
      { name: 'Node.js', icon: `skill-icons:nodejs-${theme}` },
      { name: 'Docker', icon: `skill-icons:docker` },
      { name: 'MySQL', icon: `skill-icons:mysql-${theme}` },
      { name: 'Git', icon: `skill-icons:git` },
      { name: 'GitHub', icon: `skill-icons:github-${theme}` },
      { name: 'GitLab', icon: `skill-icons:gitlab-${theme}` },
      { name: 'GraphQL', icon: `skill-icons:graphql-${theme}` },
      { name: 'Postman', icon: `skill-icons:postman` },
      { name: 'WordPress', icon: `skill-icons:wordpress` },
      { name: 'Cloudflare', icon: `skill-icons:cloudflare-${theme}` },
      { name: 'Grafana', icon: `skill-icons:grafana-${theme}` }
    ];
  }, [theme]);
  
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(getIsMobile());
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setShowAll(!isMobile);
  }, [isMobile]);

  const IconCard = ({ icon, name, index, hidden }) => (
    <motion.div
      className={`icon-card p-3 text-center rounded ${hidden ? 'icon-card-hidden' : ''}`}
      aria-hidden={hidden}
      style={{ cursor: 'pointer', ...(hidden ? { display: 'none' } : {}) }}
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        delay: index * 0.04,
        type: "spring",
        stiffness: 240,
        damping: 22
      }}
      whileHover={{ 
        scale: 1.03,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
    >
      <Iconify icon={icon} height={60} />
      <p className="m-2 icon-name">{name}</p>
    </motion.div>
  );

  const handleToggle = () => {
    setShowAll((prev) => {
      const next = !prev;
      if (!next && sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return next;
    });
  };

  return (
    <section id="skills" ref={sectionRef}>
      <Jumbotron fluid className="techskills">
        <Container>
          <motion.h2
            className="display-4 mb-5 text-center"
            initial={{ opacity: 0, y: -24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            style={{ fontWeight: 700, color: 'var(--text-primary)' }}
          >
            Tech Skills
          </motion.h2>
          <div className="techskills-grid">
            {icons.map(({ name, icon }, index) => {
              const hideOnMobile = isMobile && !showAll && index >= MOBILE_VISIBLE_COUNT;
              return (
                <IconCard
                  icon={icon}
                  name={name}
                  index={index}
                  hidden={hideOnMobile}
                  key={name}
                />
              );
            })}
          </div>
          {isMobile && (
            <div className="text-center mt-4">
              <motion.button
                className={`btn btn-lg ${
                  theme === 'light' ? 'btn-outline-dark' : 'btn-outline-light'
                }`}
                onClick={handleToggle}
                whileHover={{ scale: 1.02, boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)" }}
                whileTap={{ scale: 0.97 }}
                style={{
                  borderRadius: '12px',
                  borderWidth: '2px',
                  fontWeight: 600,
                  padding: '0.75rem 2rem'
                }}
              >
                {showAll ? 'Show Less' : 'Show More'}
              </motion.button>
            </div>
          )}
        </Container>
      </Jumbotron>
    </section>
  );
};

export default TechSkills;
